import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { formatCurrencyMinor } from '@utils/formatCurrency';
import { Price, TaxExclusion } from './Price';
export const PlanDurationSelector = ({ product, selected, onChange }) => {
    const { t, i18n } = useTranslation();
    const name = useId();
    return (_jsxs(Options, { children: [_jsx("legend", { children: t('personalCare360.chooseDuration') }), product.variants?.map((variant) => (_jsxs(Option, { "$selected": variant.id === selected?.id, children: [_jsx("input", { type: "radio", name: name, value: variant.id, checked: variant.id === selected?.id, onChange: () => onChange(variant.id) }), _jsxs(OptionBody, { children: [_jsxs(OptionTop, { children: [_jsx("strong", { children: t('personalCare360.planLabel', { months: variant.durationMonths }) }), _jsxs(OptionPrices, { children: [variant.savingsMinor > 0 && (_jsx(OriginalPrice, { "aria-label": t('personalCare360.originalPrice'), children: _jsx(Price, { amountMinor: variant.referencePriceMinor, currency: product.currency }) })), _jsx("strong", { children: _jsx(Price, { amountMinor: variant.priceMinor, currency: product.currency }) })] })] }), _jsx(OptionNote, { children: t('personalCare360.periodTotal') }), _jsx(OptionTax, { short: true }), variant.savingsMinor > 0 ? (_jsx(Saving, { children: t('personalCare360.savingsDetail', {
                                    amount: formatCurrencyMinor(variant.savingsMinor, product.currency, i18n.language),
                                    percent: variant.discountPercent
                                }) })) : variant.labelKey ? (_jsx(Saving, { children: t(variant.labelKey) })) : null, variant.bestValue && _jsx(BestValue, { children: t('personalCare360.bestValue') })] })] }, variant.id)))] }));
};
const Options = styled.fieldset `
  min-width: 0;
  border: 0;
  margin: 0 0 1.75rem;
  padding: 0;
  legend {
    margin-bottom: 1rem;
    padding: 0;
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.navy900};
  }
`;
const Option = styled.label `
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.blue600 : theme.colors.line)};
  border-left-width: 3px;
  background: ${({ theme, $selected }) => ($selected ? theme.colors.mint50 : theme.colors.white)};
  padding: 1rem;
  margin-bottom: 0.65rem;
  cursor: pointer;
  input {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin: 0.2rem 0 0;
    accent-color: ${({ theme }) => theme.colors.blue600};
  }
`;
const OptionBody = styled.span `
  display: block;
  min-width: 0;
  flex: 1;
`;
const OptionTop = styled.span `
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.35rem 1rem;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 1rem;
  strong:last-child {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;
const OptionNote = styled.span `
  display: block;
  margin-top: 0.15rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.muted};
`;
const OptionTax = styled(TaxExclusion) `
  display: block;
  margin-top: 0.1rem;
`;
const OptionPrices = styled.span `
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25rem 0.6rem;
`;
const OriginalPrice = styled.s `
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  white-space: nowrap;
  text-decoration-thickness: 1px;
`;
const Saving = styled.span `
  display: block;
  margin-top: 0.55rem;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.blue600};
`;
const BestValue = styled.span `
  display: inline-block;
  margin-top: 0.6rem;
  padding: 0.3rem 0.55rem;
  background: ${({ theme }) => theme.colors.mint200};
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.navy900};
`;
